import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, LoaderCircle, LogIn } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLoginActions } from './use-login-actions'
import type { AuthProvider, LoginStatus } from './use-login-actions'
import { env } from '@/env'
import { resolveApiUrl } from '@/shared/lib/api-url'
import { appTitle } from '@/shared/config/app-config'
import { Button } from '@/shared/ui/button'
import { LegalDocumentLinks } from '@/features/legal/legal-document-links'

type ProviderDiscovery = {
  enabled: boolean
  providers: Array<AuthProvider>
  blockers: Array<string>
}

export function LoginPage() {
  const { t } = useTranslation()
  const returnTo = useMemo(() => getSafeReturnUrl(), [])
  const discoveryState = useProviderDiscovery(t)
  const providers = discoveryState.discovery?.providers ?? []
  const loginState = useLoginActions({ providers, returnTo, t })
  const status =
    loginState.status === 'signing-in'
      ? loginState.status
      : discoveryState.status
  const errorMessage = loginState.errorMessage ?? discoveryState.errorMessage
  const autheliaProvider = providers.find(
    (provider) => provider.id === 'authelia',
  )

  return (
    <LoginPageView
      errorMessage={errorMessage}
      onCancel={loginState.cancelSignIn}
      onCancelLinkProposal={discoveryState.cancelLinkProposal}
      onLinkAccounts={() => {
        if (autheliaProvider) void loginState.linkAccounts(autheliaProvider)
      }}
      onSignIn={loginState.signIn}
      activeProviderId={loginState.activeProviderId}
      linkProposal={discoveryState.linkProposal}
      canLinkAccounts={Boolean(autheliaProvider)}
      providers={providers}
      status={status}
      t={t}
    />
  )
}

function useProviderDiscovery(t: ReturnType<typeof useTranslation>['t']) {
  const [discovery, setDiscovery] = useState<ProviderDiscovery | null>(null)
  const [status, setStatus] = useState<LoginStatus>('loading')
  const [linkProposal, setLinkProposal] = useState(isLinkProposalCallback())
  const [errorMessage, setErrorMessage] = useState<string | null>(() =>
    isLinkProposalCallback() ? null : getCallbackError(t),
  )

  const cancelLinkProposal = useCallback(() => {
    setLinkProposal(false)
    setErrorMessage(null)
    const url = new URL(window.location.href)
    url.searchParams.delete('error')
    url.searchParams.delete('error_description')
    window.history.replaceState(window.history.state, '', url)
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    void fetch(resolveApiUrl(env.VITE_API_URL, '/auth/providers'), {
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            `Provider discovery failed with status ${response.status}.`,
          )
        }

        return (await response.json()) as ProviderDiscovery
      })
      .then((nextDiscovery) => {
        setDiscovery(nextDiscovery)
        setStatus(nextDiscovery.providers.length > 0 ? 'ready' : 'error')
        if (nextDiscovery.providers.length === 0 && !getCallbackError(t)) {
          setErrorMessage(
            nextDiscovery.enabled
              ? t('auth:noProviders')
              : t('auth:authenticationUnavailable'),
          )
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return

        setStatus('error')
        setErrorMessage(
          error instanceof Error ? error.message : t('auth:discoveryFailed'),
        )
      })

    return () => controller.abort()
  }, [t])

  return { cancelLinkProposal, discovery, errorMessage, linkProposal, status }
}

function LoginPageView({
  errorMessage,
  onCancel,
  onCancelLinkProposal,
  onLinkAccounts,
  onSignIn,
  activeProviderId,
  canLinkAccounts,
  linkProposal,
  providers,
  status,
  t,
}: {
  activeProviderId: string | null
  canLinkAccounts: boolean
  errorMessage: string | null
  linkProposal: boolean
  onCancel: () => void
  onCancelLinkProposal: () => void
  onLinkAccounts: () => void
  onSignIn: (provider: AuthProvider) => Promise<boolean>
  providers: Array<AuthProvider>
  status: LoginStatus
  t: ReturnType<typeof useTranslation>['t']
}) {
  const providerButtons = providers.length > 0 ? providers : [undefined]

  return (
    <main className="min-h-screen bg-radial-[at_top] from-accent/50 via-background to-background px-6 py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-3xl items-center justify-center">
        <section className="w-full max-w-md rounded-[2rem] border border-border/70 bg-background/90 p-8 shadow-2xl shadow-primary/10 backdrop-blur sm:p-10">
          <LoginHeading linkProposal={linkProposal} status={status} t={t} />

          <LoginError message={errorMessage} />
          {linkProposal && !canLinkAccounts && !errorMessage ? (
            <p className="mt-6 text-sm text-muted-foreground" role="status">
              {t('auth:linkUnavailable')}
            </p>
          ) : null}

          <div className="mt-8 flex flex-col gap-3">
            {linkProposal ? (
              <LinkProposalActions
                canLinkAccounts={canLinkAccounts}
                onCancel={() => {
                  if (status === 'signing-in') onCancel()
                  onCancelLinkProposal()
                }}
                onLinkAccounts={onLinkAccounts}
                status={status}
                t={t}
              />
            ) : (
              providerButtons.map((provider, index) => (
                <Button
                  disabled={
                    !provider || status === 'loading' || status === 'signing-in'
                  }
                  key={index === 0 ? 'provider-button' : provider?.id}
                  onClick={() => {
                    if (provider) void onSignIn(provider)
                  }}
                  size="lg"
                >
                  {status === 'signing-in' && provider?.id === activeProviderId
                    ? t('auth:signingIn')
                    : getProviderLabel(provider, t)}
                </Button>
              ))
            )}
            {!linkProposal && status === 'signing-in' ? (
              <Button onClick={onCancel} variant="outline">
                {t('common:cancel')}
              </Button>
            ) : null}
          </div>
          <LegalDocumentLinks />
        </section>
      </div>
    </main>
  )
}

function LoginError({ message }: { message: string | null }) {
  if (!message) return null

  return (
    <p
      aria-live="polite"
      className="mt-6 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
    >
      {message}
    </p>
  )
}

function LoginHeading({
  linkProposal,
  status,
  t,
}: {
  linkProposal: boolean
  status: LoginStatus
  t: ReturnType<typeof useTranslation>['t']
}) {
  return (
    <>
      <div className="mb-8 flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        {getLoginIcon(status)}
      </div>
      <div className="space-y-3">
        <p className="text-sm font-semibold uppercase tracking-[0.32em] text-primary">
          {appTitle}
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">
          {t(linkProposal ? 'auth:linkAccountsTitle' : 'auth:signInTitle')}
        </h1>
        <p className="text-base leading-7 text-muted-foreground">
          {t(
            linkProposal
              ? 'auth:linkAccountsDescription'
              : 'auth:signInDescription',
          )}
        </p>
      </div>
    </>
  )
}

function LinkProposalActions({
  canLinkAccounts,
  onCancel,
  onLinkAccounts,
  status,
  t,
}: {
  canLinkAccounts: boolean
  onCancel: () => void
  onLinkAccounts: () => void
  status: LoginStatus
  t: ReturnType<typeof useTranslation>['t']
}) {
  return (
    <>
      <Button
        disabled={!canLinkAccounts || status === 'signing-in'}
        onClick={onLinkAccounts}
        size="lg"
      >
        {status === 'signing-in' ? t('auth:signingIn') : t('auth:linkAccounts')}
      </Button>
      <Button onClick={onCancel} variant="outline">
        {t('common:cancel')}
      </Button>
    </>
  )
}

function getProviderLabel(
  provider: AuthProvider | undefined,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (!provider) return t('auth:signInWithPassword')
  if (provider.id === 'authelia') return t('auth:signInWithPassword')
  if (provider.id === 'yandex') return t('auth:signInWithYandex')

  return provider.name
}

function getLoginIcon(status: LoginStatus) {
  if (status === 'signing-in') {
    return <LoaderCircle className="size-7 animate-spin" />
  }

  if (status === 'error') {
    return <AlertTriangle className="size-7" />
  }

  return <LogIn className="size-7" />
}

function getSafeReturnUrl(): string {
  if (typeof window === 'undefined') return '/'

  const currentUrl = new URL(window.location.href)
  const signedRequest = currentUrl.searchParams
  if (
    signedRequest.has('client_id') &&
    signedRequest.has('sig') &&
    signedRequest.has('ba_iat')
  ) {
    const consent = new URL('/mcp/authorize', currentUrl.origin)
    consent.search = currentUrl.search

    return consent.toString()
  }
  const returnTo = currentUrl.searchParams.get('returnTo')

  if (returnTo) {
    try {
      const requestedUrl = new URL(returnTo, currentUrl.origin)
      if (
        requestedUrl.origin === currentUrl.origin &&
        requestedUrl.pathname !== '/login'
      ) {
        return requestedUrl.toString()
      }
    } catch {
      // Invalid return destinations fall back to the application root.
    }
  }

  if (currentUrl.pathname !== '/login') {
    return currentUrl.toString()
  }

  return `${currentUrl.origin}/`
}

function getCallbackError(
  t: ReturnType<typeof useTranslation>['t'],
): string | null {
  if (typeof window === 'undefined') return null

  const params = new URL(window.location.href).searchParams
  const reason = params.get('reason')
  const error = params.get('error_description') ?? params.get('error')

  if (reason === 'session_expired') return t('auth:sessionExpired')

  return error ? decodeURIComponentSafe(error) : null
}

function isLinkProposalCallback(): boolean {
  if (typeof window === 'undefined') return false

  return (
    new URL(window.location.href).searchParams.get('error') ===
    'existing_user_requires_linking'
  )
}

function decodeURIComponentSafe(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}
